/**
 * CartBar — the sticky checkout panel for the in-category cart (ТЗ §9).
 *
 * Appears only when at least one product is selected. Shows the unit count and the
 * server-computed total ("Выбрано: N товара · <сумма> ₽"), plus «Отменить» (clear the
 * selection) and «Оформить заказ» (create the order). The total comes verbatim from
 * ``/api/cart/quote`` — this widget never sums prices itself.
 */
import { formatRub } from "@shared/lib/format";

import styles from "./CartBar.module.css";

export interface CartBarProps {
  /** Total units selected (drives the "N товаров" label). */
  readonly count: number;
  /** Server-computed total (RUB Decimal string), or ``null`` while first quoting. */
  readonly totalRub: string | null;
  /** A re-quote is in flight (debounced). */
  readonly isQuoting: boolean;
  /** Checkout request is in flight. */
  readonly isSubmitting: boolean;
  /** Human error text (quote/checkout), if any. */
  readonly error: string | null;
  readonly onCancel: () => void;
  readonly onCheckout: () => void;
}

function itemsWord(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) {
    return "товар";
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return "товара";
  }
  return "товаров";
}

export function CartBar({
  count,
  totalRub,
  isQuoting,
  isSubmitting,
  error,
  onCancel,
  onCheckout,
}: CartBarProps): JSX.Element {
  const totalLabel =
    totalRub !== null ? formatRub(totalRub) : isQuoting ? "…" : "—";
  const canCheckout = totalRub !== null && !isQuoting && !isSubmitting;

  return (
    <div className={styles.wrap} role="region" aria-label="Корзина">
      <div className={styles.inner}>
        {error !== null ? (
          <p className={styles.error} role="alert">
            {error}
          </p>
        ) : null}
        <div className={styles.summary}>
          <span className={styles.count}>
            Выбрано: {count} {itemsWord(count)}
          </span>
          <span className={styles.total}>{totalLabel}</span>
        </div>
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.cancel}
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Отменить
          </button>
          <button
            type="button"
            className={styles.checkout}
            onClick={onCheckout}
            disabled={!canCheckout}
          >
            {isSubmitting ? "Оформляем…" : "Оформить заказ"}
          </button>
        </div>
      </div>
    </div>
  );
}
